import React, { useEffect, useState } from "react";
import { apiJson } from "../../utils/api";

const AdminDashboard = ({ onNavigate }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const admin = JSON.parse(localStorage.getItem("admin") || "{}");

  useEffect(() => {
    apiJson("/api/admin/dashboard")
      .then(setData)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="max-w-5xl mx-auto">
        <h1 className="text-2xl font-bold text-gray-800 mb-2">Admin Dashboard</h1>
        <p className="text-gray-500">Unable to load dashboard data.</p>
      </div>
    );
  }

  const { summary, activeSession, recentAdmissions } = data;

  const summaryCards = [
    {
      label: "Total Admissions",
      value: summary.totalAdmissions,
      subtitle: activeSession ? activeSession.name : "No active session",
      color: "text-indigo-600",
      bg: "bg-indigo-50",
      icon: (
        <svg className="w-6 h-6 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      ),
    },
    {
      label: "Pending Verification",
      value: summary.pendingVerifications,
      subtitle: "Awaiting review",
      color: "text-amber-600",
      bg: "bg-amber-50",
      highlight: summary.pendingVerifications > 0,
      icon: (
        <svg className="w-6 h-6 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
    },
    {
      label: "Admitted",
      value: summary.admittedStudents,
      subtitle: `${summary.rejectedCount} rejected`,
      color: "text-green-600",
      bg: "bg-green-50",
      icon: (
        <svg className="w-6 h-6 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
    },
    {
      label: "Revenue Collected",
      value: summary.totalRevenue.toLocaleString("en-IN", {
        style: "currency",
        currency: "INR",
        maximumFractionDigits: 0,
      }),
      subtitle: "Successful payments",
      color: "text-emerald-600",
      bg: "bg-emerald-50",
      icon: (
        <svg className="w-6 h-6 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
    },
  ];

  const quickActions = [
    {
      label: "Review Admissions",
      description: `${summary.pendingVerifications} pending verification`,
      nav: "admission-list",
      color: "bg-amber-50 border-amber-200 text-amber-700",
      iconBg: "bg-amber-100",
      badge: summary.pendingVerifications > 0 ? summary.pendingVerifications : null,
      icon: (
        <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
        </svg>
      ),
    },
    {
      label: "Merit List",
      description: `${summary.meritListCount} entries uploaded`,
      nav: "merit-list",
      color: "bg-blue-50 border-blue-200 text-blue-700",
      iconBg: "bg-blue-100",
      icon: (
        <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      ),
    },
    {
      label: "Fee Rules",
      description: "Manage fee structure",
      nav: "fee-rules",
      color: "bg-emerald-50 border-emerald-200 text-emerald-700",
      iconBg: "bg-emerald-100",
      icon: (
        <svg className="w-5 h-5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      ),
    },
    {
      label: "Academic Sessions",
      description: "Manage sessions",
      nav: "academic-sessions",
      color: "bg-purple-50 border-purple-200 text-purple-700",
      iconBg: "bg-purple-100",
      icon: (
        <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      ),
    },
  ];

  const statusColor = {
    PENDING: "bg-yellow-100 text-yellow-700",
    FORM_SUBMITTED: "bg-blue-100 text-blue-700",
    PAYMENT_DONE: "bg-purple-100 text-purple-700",
    VERIFIED: "bg-teal-100 text-teal-700",
    ADMITTED: "bg-green-100 text-green-700",
    REJECTED: "bg-red-100 text-red-700",
  };

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Admin Dashboard</h1>
          <p className="text-gray-500 text-sm mt-1">
            Welcome back, {admin.name}
            {activeSession ? ` \u00B7 Session: ${activeSession.name}` : ""}
          </p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {summaryCards.map((card) => (
          <div
            key={card.label}
            className={`rounded-lg shadow p-5 ${card.bg} ${
              card.highlight ? "ring-2 ring-amber-300" : ""
            }`}
          >
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                {card.label}
              </p>
              {card.icon}
            </div>
            <p className={`text-3xl font-bold ${card.color}`}>{card.value}</p>
            <p className="text-xs text-gray-500 mt-1">{card.subtitle}</p>
          </div>
        ))}
      </div>

      {/* Pipeline breakdown */}
      <div className="bg-white rounded-lg shadow p-5">
        <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">
          Admission Pipeline
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          {[
            { label: "Pending", count: summary.totalAdmissions - summary.formSubmittedCount - summary.paymentDoneCount - summary.admittedStudents - summary.rejectedCount, color: "bg-yellow-400" },
            { label: "Form Submitted", count: summary.formSubmittedCount, color: "bg-blue-400" },
            { label: "Payment Done", count: summary.paymentDoneCount, color: "bg-purple-400" },
            { label: "Admitted", count: summary.admittedStudents, color: "bg-green-400" },
            { label: "Rejected", count: summary.rejectedCount, color: "bg-red-400" },
          ].map((stage) => (
            <div key={stage.label} className="text-center">
              <div className={`h-2 rounded-full ${stage.color} mb-2`} />
              <p className="text-xl font-bold text-gray-700">{Math.max(0, stage.count)}</p>
              <p className="text-xs text-gray-500">{stage.label}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Quick Actions */}
        <div>
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
            Quick Actions
          </h2>
          <div className="space-y-3">
            {quickActions.map((action) => (
              <button
                key={action.nav}
                onClick={() => onNavigate(action.nav)}
                className={`w-full flex items-center gap-3 p-4 rounded-lg border text-left hover:shadow-md transition ${action.color}`}
              >
                <div className={`p-2 rounded-lg ${action.iconBg}`}>{action.icon}</div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <p className="font-semibold text-sm">{action.label}</p>
                    {action.badge && (
                      <span className="bg-amber-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                        {action.badge}
                      </span>
                    )}
                  </div>
                  <p className="text-xs opacity-75 mt-0.5">{action.description}</p>
                </div>
                <svg className="w-5 h-5 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            ))}
          </div>
        </div>

        {/* Recent Admissions */}
        <div>
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
            Recent Admissions
          </h2>
          <div className="bg-white rounded-lg shadow overflow-hidden">
            {recentAdmissions.length === 0 ? (
              <p className="p-4 text-gray-400 text-sm">No admissions yet.</p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 text-gray-500 text-xs uppercase">
                    <th className="text-left px-4 py-2">Student</th>
                    <th className="text-left px-4 py-2">Degree</th>
                    <th className="text-left px-4 py-2">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {recentAdmissions.map((adm) => (
                    <tr key={adm.id} className="hover:bg-gray-50">
                      <td className="px-4 py-2.5">
                        <p className="font-medium text-gray-800">{adm.student.name}</p>
                        <p className="text-xs text-gray-400">{adm.student.applyId}</p>
                      </td>
                      <td className="px-4 py-2.5 text-gray-600">{adm.degree.name}</td>
                      <td className="px-4 py-2.5">
                        <span
                          className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                            statusColor[adm.status] || "bg-gray-100 text-gray-600"
                          }`}
                        >
                          {adm.status.replace(/_/g, " ")}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
